import type { LucideIcon } from "lucide-react";
import {
  Blocks,
  Compass,
  Eye,
  LayoutGrid,
} from "lucide-react";

export const company = {
  name: "Vorseum",
  domain: "www.vorseum.com",
  siteUrl: "https://www.vorseum.com",
  // VOR-104: a antiga landing /zenthor virou modal de waitlist
  // (`<WaitlistTrigger>`). `productUrl` agora é a âncora pra seção
  // Pré-lançamento Zenthor da home — usado em links de menu/footer.
  productUrl: "/#zenthor",
  productHost: "zenthor.vorseum.com",
};

/**
 * Redes sociais públicas exibidas no footer (VOR-44).
 *
 * Notas de handles:
 * - Instagram usa `vors.eum` (com ponto). `@vorseum` cru está bloqueado e
 *   só será reivindicado pós-CNPJ via INPI.
 * - Demais redes usam `vorseum`/`@vorseum` direto.
 *
 * WhatsApp: número pessoal de Hannah temporariamente como contato Vorseum.
 * Migrar pro WhatsApp Business profissional quando rolar.
 */
export const contact = {
  email: "contato@vorseum.com",
  social: [
    {
      id: "linkedin",
      label: "LinkedIn",
      href: "https://linkedin.com/company/vorseum",
    },
    {
      id: "instagram",
      label: "Instagram",
      href: "https://instagram.com/vors.eum",
    },
    { id: "tiktok", label: "TikTok", href: "https://tiktok.com/@vorseum" },
    { id: "x", label: "X (Twitter)", href: "https://x.com/vorseum" },
    { id: "reddit", label: "Reddit", href: "https://reddit.com/user/vorseum" },
    { id: "whatsapp", label: "WhatsApp", href: "https://wa.me/5581988336699" },
  ],
} as const;

export type SocialLink = (typeof contact.social)[number];

// VOR-64: navbar reduzida ao essencial — Produtos + Studio + Sobre a empresa.
// "Início" some do menu (logo já leva pra raiz), "Diferenciais" continua
// sendo seção visitável via scroll mas fica fora do menu pra dar foco.
// CTA "Entrar em contato" fica fora dessa lista (renderizado direto no
// Header como botão destacado).
//
// Serviços empacotados (productized do Studio) entram como sub-item de
// Studio no header — não como item top-level, pra não competir visualmente
// com os produtos próprios (Zenthor, Hlín, Forgeum). Quando a Vorseum
// virar empresa estabelecida com receita constante, considerar mover
// Serviços pra item top-level.
// VOR-124 (2026-05-15) — Bea pediu pra eliminar a página /servicos
// separada. Conteúdo agora vive numa seção dentro de /studio.
export const navigation = [
  // VOR-132 — Início como primeiro item, leva pra root do vorseum.com
  { label: "Início", href: "/" },
  { label: "Produtos", href: "/produtos" },
  { label: "Studio", href: "/studio" },
  { label: "Sobre a empresa", href: "/#sobre" },
] as const;

export type Product = {
  id: string;
  name: string;
  status: string;
  tagline: string;
  description: string;
  benefits: {
    title: string;
    description: string;
  }[];
  href?: string;
  ctaLabel: string;
  ctaHref: string;
  external?: boolean;
  note?: string;
  /**
   * Data ISO (YYYY-MM-DD) da última mudança significativa pública do produto.
   * Sinal de "produto vivo" no card. Atualizar manualmente quando houver
   * release, mudança de copy ou marco visível ao público.
   */
  lastUpdate?: string;
};

export const products: Product[] = [
  {
    id: "naira",
    name: "Naira",
    status: "Em desenvolvimento",
    tagline: "IA soberana brasileira, treinada do zero, com os seus dados morando no Brasil.",
    description:
      "A Naira é um modelo de linguagem próprio da Vorseum, construído do zero em português, para levar inteligência artificial sem depender de modelos estrangeiros e mantendo os dados no país.",
    benefits: [
      {
        title: "Soberania de dados",
        description:
          "Modelo e dados residentes no Brasil, sob a LGPD, sem depender de APIs de fora do país.",
      },
      {
        title: "Construída do zero",
        description:
          "Arquitetura própria em vez de um modelo estrangeiro adaptado, com tokenizador nativo em português.",
      },
      {
        title: "Segurança comprovável",
        description:
          "Camada de segurança e ética embutida por estrutura, com decisões auditáveis.",
      },
    ],
    ctaLabel: "Falar sobre Naira",
    ctaHref: "/contato",
    note: "Em desenvolvimento; modelo soberano em construção.",
    lastUpdate: "2026-07-01",
  },
  {
    id: "zenthor",
    name: "Zenthor",
    status: "Disponível",
    tagline: "Monitoramento de exposição digital para quem precisa agir sem leitura técnica pesada.",
    description:
      "O Zenthor foi criado para ajudar pessoas e pequenas operações a verificar vazamentos, acompanhar sinais de risco e entender o que merece atenção primeiro.",
    benefits: [
      {
        title: "Verificação de exposição",
        description:
          "Veja se há sinais de vazamentos e exposição de dados sem depender de uma investigação manual extensa.",
      },
      {
        title: "Monitoramento contínuo",
        description:
          "Acompanhe novos riscos ao longo do tempo sem voltar para buscas repetitivas.",
      },
      {
        title: "Leitura objetiva",
        description:
          "Receba contexto suficiente para decidir o próximo passo com menos ruído.",
      },
    ],
    href: company.productUrl,
    ctaLabel: "Acessar o Zenthor",
    ctaHref: "https://zenthor.vorseum.com",
    external: true,
    note: "No ar: comece a monitorar sua exposição digital agora.",
    lastUpdate: "2026-06-17",
  },
  // VOR-78: ordem da seção Produtos — Zenthor, Forgeum (ao meio), Hlín.
  {
    id: "forgeum",
    name: "Forgeum",
    status: "Beta interno",
    tagline: "Wiki viva e base de conhecimento para times que tratam documentação como produto.",
    description:
      "O Forgeum reúne documentação, design system e operação editorial em um mesmo fluxo para times que não querem conhecimento espalhado em ferramentas desconectadas.",
    benefits: [
      {
        title: "Conhecimento vivo",
        description:
          "Estrutura para manter decisões, processos e contexto acessíveis conforme o produto evolui.",
      },
      {
        title: "Design system conectado",
        description:
          "Espaço para alinhar componentes, conteúdo e referências sem separar demais o trabalho do time.",
      },
      {
        title: "Fluxo editorial prático",
        description:
          "Uma base para organizar conteúdo interno sem transformar documentação em arquivo morto.",
      },
    ],
    ctaLabel: "Falar sobre Forgeum",
    ctaHref: "/contato",
    note: "Hoje em uso interno e ainda fechado ao público.",
    lastUpdate: "2026-05-01",
  },
  {
    id: "hlin",
    name: "Hlín",
    status: "Em desenvolvimento",
    tagline: "Mobilidade urbana acessível com IA, para quem precisa conhecer a rua antes de chegar nela.",
    description:
      "A Hlín está em desenvolvimento ativo para pedestres com deficiência, idosos e quem se desloca pela cidade sem saber o que vem pela frente. Mapeia a acessibilidade de calçadas, travessias e obstáculos, com apoio de IA para organizar cada contribuição.",
    benefits: [
      {
        title: "Mapa de acessibilidade",
        description:
          "Calçadas, travessias e obstáculos descritos pela comunidade, para decidir o trajeto antes de sair de casa.",
      },
      {
        title: "Contribuição organizada por IA",
        description:
          "Cada relato passa por uma checagem assistida por IA, com as decisões registradas de forma transparente e auditável.",
      },
      {
        title: "Rotas para o percurso real",
        description:
          "Apoio para encontrar caminhos viáveis para cadeira de rodas, baixa visão e mobilidade reduzida, com menos surpresa no caminho.",
      },
    ],
    ctaLabel: "Falar sobre Hlín",
    ctaHref: "/contato",
    note: "Em desenvolvimento ativo; ainda não aberto ao público geral.",
    lastUpdate: "2026-06-09",
  },
];

/**
 * VOR-101 — nova versão da seção Sobre (curada pela Bea).
 *
 * Estrutura em três blocos:
 *   1. Narrativa — quem é a Vorseum (humanidade + AI-first).
 *   2. Valores inegociáveis — três cards (Humanidade, Sofisticação,
 *      Movimento) com tom mais largo, "produtos do futuro guiados por
 *      princípios ancestrais".
 *   3. CTA final — "O que você quer fazer acontecer? A Vorseum ajuda
 *      a encontrar a forma." → /contato.
 */
export const aboutContent = {
  eyebrow: "Sobre a Vorseum",
  title: "Uma narrativa que atravessa o tempo",
  // VOR-101 follow-up (2026-05-10): subtitle removido a pedido da Bea
  // ("Remover subtitle 'Porque antes da tecnologia, sempre houve humanidade'").
  paragraphs: [
    "A Vorseum é uma empresa humana, construída com mentalidade AI-first. Usamos tecnologias avançadas para criar produtos inteligentes, sem jamais esquecer que tudo isso existe para servir pessoas.",
    "Inspirados pelo vórtice que dá forma à nossa marca, convergimos tecnologia, design e sofisticação para desenvolver soluções que sustentam nosso propósito: criar com ousadia, sensibilidade e respeito à não linearidade humana.",
  ],
  values: {
    title: "Valores inegociáveis",
    subtitle: "Produtos do futuro, guiados por princípios ancestrais",
    items: [
      {
        title: "Humanidade",
        description: "Antes de automatizar, entendemos pessoas",
      },
      {
        title: "Sofisticação",
        description: "Criamos com clareza e intenção",
      },
      {
        title: "Movimento",
        description: "Damos forma ao essencial e evoluímos com sistemas vivos",
      },
    ],
  },
  cta: {
    title: "O que você quer fazer acontecer?",
    subtitle: "A Vorseum ajuda a encontrar a forma.",
    label: "Entrar em contato",
    href: "/contato",
  },
};

export const manifestoPrinciples = [
  {
    title: "Segurança não precisa falar difícil",
    description:
      "Quando o assunto é sério, a interface precisa ajudar, não intimidar. Se a pessoa precisa traduzir a tela antes de agir, o produto ainda não terminou.",
  },
  {
    title: "Cada produto tem que merecer existir",
    description:
      "Não queremos um portfólio cheio de labels vazias. Cada software precisa ter problema claro, voz própria e um motivo real para continuar no ar.",
  },
  {
    title: "IA entra para reduzir trabalho, não para decorar tela",
    description:
      "A novidade sozinha não nos interessa. IA só faz sentido quando corta etapas, amplia contexto ou ajuda alguém a tomar uma decisão melhor.",
  },
  {
    title: "Documento bom também é parte do produto",
    description:
      "Não tratamos documentação como apêndice. Texto útil, contexto acessível e sistema vivo fazem parte da experiência que sustenta o software no tempo.",
  },
] as const;

export type Feature = {
  title: string;
  description: string;
  icon: LucideIcon;
};

export const features: Feature[] = [
  {
    title: "Começar pelo problema",
    description:
      "O produto nasce da tarefa real e do atrito concreto. A categoria de mercado entra depois, se ainda fizer sentido.",
    icon: LayoutGrid,
  },
  {
    title: "Texto que ajuda",
    description:
      "A linguagem precisa ajudar alguém a decidir, navegar e entender. Copy boa não faz pose; ela reduz ruído.",
    icon: Compass,
  },
  {
    title: "Identidade sem disfarce",
    description:
      "Zenthor, Hlín e Forgeum têm ritmos, propostas e públicos próprios. A Vorseum conecta o portfólio sem nivelar tudo por cima.",
    icon: Eye,
  },
  {
    title: "Produto que continua vivo",
    description:
      "Produto, conteúdo, design e engenharia evoluem juntos. O objetivo é software que continue útil depois do lançamento, não só na semana da estreia.",
    icon: Blocks,
  },
];

